import { useEffect, useRef, useState } from "react";
import type { GalleryVideo } from "@/components/gallery/GalleryVideoCard";
import { getGalleryStreamUrl } from "@/api/galleriesApi";

export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

interface UseGallerySSEOptions {
  /** Gallery slug. Pass null to disable the connection. */
  slug: string | null;
  /** Called when a new video event is received. */
  onVideoNew: (video: GalleryVideo) => void;
  /** Called when gallery settings are updated via SSE. */
  onGalleryUpdated?: (config: Record<string, unknown>) => void;
  /** Called when gallery is deactivated via SSE. */
  onGalleryDeactivated?: () => void;
}

interface UseGallerySSEReturn {
  connectionStatus: ConnectionStatus;
}

const HEARTBEAT_TIMEOUT_MS = 60_000;

/**
 * Calculate reconnection delay with exponential backoff and jitter.
 * Exported for unit testing.
 */
export function getReconnectDelay(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, 30_000);
  // ±20% jitter
  const jitter = base * 0.2 * (2 * Math.random() - 1);
  return Math.round(base + jitter);
}

export function useGallerySSE({
  slug,
  onVideoNew,
  onGalleryUpdated,
  onGalleryDeactivated,
}: UseGallerySSEOptions): UseGallerySSEReturn {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const lastEventIdRef = useRef<string | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef(0);

  // Stable refs for callbacks — avoids tearing down/recreating the SSE
  // connection when callback identities change (e.g. muted toggle).
  const onVideoNewRef = useRef(onVideoNew);
  onVideoNewRef.current = onVideoNew;
  const onGalleryUpdatedRef = useRef(onGalleryUpdated);
  onGalleryUpdatedRef.current = onGalleryUpdated;
  const onGalleryDeactivatedRef = useRef(onGalleryDeactivated);
  onGalleryDeactivatedRef.current = onGalleryDeactivated;

  useEffect(() => {
    if (!slug) {
      setConnectionStatus("disconnected");
      return;
    }

    let disposed = false;

    const connect = () => {
      if (disposed) return;

      setConnectionStatus(
        reconnectAttemptRef.current > 0 ? "reconnecting" : "connecting",
      );

      const streamUrl = getGalleryStreamUrl(slug, lastEventIdRef.current);

      const es = new EventSource(streamUrl, { withCredentials: true });
      eventSourceRef.current = es;

      const resetHeartbeat = () => {
        if (heartbeatTimerRef.current) clearTimeout(heartbeatTimerRef.current);
        heartbeatTimerRef.current = setTimeout(() => {
          // No heartbeat received — reconnect
          es.close();
          scheduleReconnect();
        }, HEARTBEAT_TIMEOUT_MS);
      };

      const scheduleReconnect = () => {
        if (disposed) return;
        setConnectionStatus("reconnecting");
        const delay = getReconnectDelay(reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      es.addEventListener("connected", () => {
        reconnectAttemptRef.current = 0;
        setConnectionStatus("connected");
        resetHeartbeat();
      });

      es.addEventListener("video:new", (event: MessageEvent) => {
        resetHeartbeat();
        if (event.lastEventId) {
          lastEventIdRef.current = event.lastEventId;
        }
        try {
          const data = JSON.parse(event.data);
          onVideoNewRef.current(data as GalleryVideo);
        } catch {
          // Ignore malformed events
        }
      });

      es.addEventListener("gallery:updated", (event: MessageEvent) => {
        resetHeartbeat();
        if (event.lastEventId) {
          lastEventIdRef.current = event.lastEventId;
        }
        try {
          const data = JSON.parse(event.data);
          onGalleryUpdatedRef.current?.(data as Record<string, unknown>);
        } catch {
          // Ignore malformed events
        }
      });

      es.addEventListener("gallery:deactivated", () => {
        es.close();
        setConnectionStatus("disconnected");
        onGalleryDeactivatedRef.current?.();
      });

      // Heartbeat/ping events from sse-starlette
      es.addEventListener("ping", () => {
        resetHeartbeat();
      });

      // Generic message handler for heartbeats
      es.onmessage = () => {
        resetHeartbeat();
      };

      es.onerror = () => {
        es.close();
        scheduleReconnect();
      };

      resetHeartbeat();
    };

    connect();

    return () => {
      disposed = true;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (heartbeatTimerRef.current) clearTimeout(heartbeatTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [slug]);

  return { connectionStatus };
}
